import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";

import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import styled, { css } from "styled-components";

const enum fields {
    listField = "listField",
    title = "title",
    description = "description",
    background = "background",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.listField]: any[];
        [fields.title]: string;
        [fields.description]: string;
        [fields.background]: string;
    };
}

type Props = OwnProps;

export interface IInfoStatsStyles {
    infoCard?: GridItemProps;
    title?: TypographyProps;
    desc?: InjectableCss;
    container?: GridContainerProps;
}

export const InfoStatsStyles: IInfoStatsStyles = {
    container: {
        css: css`
            > [class^="GridOffset-"] {
                display: flex;
                justify-content: center;
                align-items: center;
            }
        `,
    },
    infoCard: {
        width: [12, 12, 6, 3, 3],
        css: css`
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
        `,
    },
    title: {
        variant: "h5",
        css: css`
            color: ${getColor("primary")};
            text-transform: uppercase;
            font-size: 2.5rem;
            width: 100%;
            text-align: center;
            margin-bottom: 15px;
            line-height: 32px;
            white-space: nowrap;
            .subtitle {
                display: block;
                font-weight: 200;
                font-size: 15px;
            }
        `,
    },

    desc: {
        css: css`
            display: block;
            line-height: 16px;
            font-size: 13px;
            color: ${getColor("secondary")};
            font-weight: 400;
            padding: 0px 45px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding: 0 10px;
                    `,
                    null,
                ])}
            text-align: center;

            p {
                margin-bottom: 0;
            }
        `,
    },
};

const styles = InfoStatsStyles;

const DescriptionWrapper = styled.div<{ backgroundColor: string }>`
    background-color: ${props => props.backgroundColor};
    padding: 50px;
    margin: 40px auto 60px;

    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    max-width: 100% !important;
                `,
                css`
                    max-width: 100% !important;
                `,
                css`
                    max-width: 720px !important;
                `,
                css`
                    max-width: 960px !important;
                `,
                css`
                    max-width: 1300px !important;
                `,
            ],
            "min",
        )}
`;

const InfoStats: React.FC<Props> = ({ fields }) => {
    return (
        <DescriptionWrapper backgroundColor={fields.background}>
            <GridContainer {...styles.container}>
                {fields.listField.map(item => (
                    <GridItem key={item.fields.title} {...styles.infoCard}>
                        <Typography {...styles.title}>{item.fields.title}</Typography>
                        <Typography {...styles.desc}>{parse(item.fields.description, parserOptions)}</Typography>
                    </GridItem>
                ))}
            </GridContainer>
        </DescriptionWrapper>
    );
};

const membersList: WidgetModule = {
    component: InfoStats,
    definition: {
        group: "Common",

        fieldDefinitions: [
            {
                name: fields.listField,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",

                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: fields.title,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 0,
                    },

                    {
                        name: fields.description,
                        displayName: "Description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        sortOrder: 2,
                    },
                ],
            },
            {
                fieldType: "Translatable",
                name: fields.background,
                displayName: "Description Background",
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
                sortOrder: 1,
            },
        ],
    },
};

export default membersList;
